import { useEffect, useState } from 'react'
import { getAllCustomers, createCustomer, updateCustomer, deleteCustomer } from '../api'
import { invalidateCustomerCache } from '../components/invoice/InvoiceForm'
import ProcessingOverlay from '../components/ui/ProcessingOverlay'

const empty = { name: '', address: '', phone: '', email: '' }
const PAGE_SIZE = 50

export default function Customers() {
  const [allCustomers, setAllCustomers] = useState<any[]>([])
  const [form, setForm] = useState(empty)
  const [editId, setEditId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [processing, setProcessing] = useState('')
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  const load = () => {
    setLoading(true)
    getAllCustomers().then((r: any) => { setAllCustomers(r.data); setLoading(false) }).catch(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const filtered = allCustomers
    .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.address || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => b.id - a.id)

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [search])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) return
    setProcessing(editId ? 'Updating customer...' : 'Saving customer...')
    try {
      if (editId) { await updateCustomer(editId, form) }
      else { await createCustomer(form) }
      invalidateCustomerCache()
      setForm(empty); setEditId(null); setShowForm(false); load()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Save failed')
    } finally { setProcessing('') }
  }

  const startEdit = (c: any) => {
    setEditId(c.id)
    setForm({ name: c.name, address: c.address || '', phone: c.phone || '', email: c.email || '' })
    setShowForm(true)
  }

  return (
    <div className="page">
      {processing && <ProcessingOverlay message={processing} />}
      <div className="page-header">
        <div>
          <div className="page-title">Customers</div>
          <div className="page-subtitle">Manage customer master data ({allCustomers.length} total)</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(empty); setEditId(null); setShowForm(true) }}>
          + Add Customer
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>{editId ? 'Edit Customer' : 'New Customer'}</div>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 2fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Name *</label>
                <input className="form-input" value={form.name} required onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Address</label>
                <input className="form-input" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Email</label>
                <input className="form-input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="btn btn-primary">{editId ? 'Update' : 'Save'}</button>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); setForm(empty) }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'end' }}>
          <div className="search-bar" style={{ flex: 1 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-primary" style={{ height: 38 }} onClick={load}>🔍 Search</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 48 }}>Loading customers...</div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr><th>Name</th><th>Address</th><th>Phone</th><th>Email</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {paginated.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No customers found</td></tr>}
                {paginated.map((c: any) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td style={{ color: '#64748b' }}>{c.address || '—'}</td>
                    <td style={{ color: '#64748b' }}>{c.phone || '—'}</td>
                    <td style={{ color: '#64748b' }}>{c.email || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => startEdit(c)}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={async () => {
                          if (window.confirm('Delete?')) {
                            setProcessing('Deleting customer...')
                            try { await deleteCustomer(c.id); invalidateCustomerCache(); load() } finally { setProcessing('') }
                          }
                        }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '8px 0' }}>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  <span style={{ padding: '4px 12px', fontSize: 13, color: '#64748b' }}>Page {page} / {totalPages}</span>
                  <button className="btn btn-sm btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
